// (C) Copyright 2025 Hewlett Packard Enterprise Development LP

import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import omit from 'lodash/omit'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useReactOidc } from '@axa-fr/react-oidc-context'

import { FilterButton } from '../../../../../../components'
import { isGLOP } from '../../../../../../utils/feature-flag-utils'
import {
  getSubscriptionFilterOptions,
  getSubscriptionTiers,
  queryParamsBasedOnTab
} from '../../../../../../utils/dm-sm-common-utils'
import { displayApiError } from '../../../../../../utils/error-handling-utils'
import { getTags } from '../../../utils'

const SubscriptionsFilterButton = ({
  tabName = 'totalSubscriptions',
  productType = 'DEVICE',
  onFilterValueChange,
  refreshTags,
  filterOptions
}) => {
  const { t } = useTranslation(['device', 'licensing', 'common'])
  const LDFlags = useFlags()
  const {
    'glcp-dm-silver-peak': dmSilverPeakFlag,
    'glcp-dm-uxi': dmUXIFlag,
    'glcp-dm-pce': dmPCEFlag, // PCE LD is added to check PCE workflow in StandAlone Account just for testing purpose untill all services support PCE changes
    'glcp-5g-bridge': dmBridgeFlag,
    'glcp-subscrption-tags': subscriptionTagsFlag,
    'glcp-service-subscription-tiers': serviceSubscriptionTiersLD
  } = LDFlags
  const { oidcUser } = useReactOidc()

  // Stores subscription list response
  const [subscriptionTierList, setSubscriptionTierList] = useState([])
  // Stores tag list response
  const [allTagsList, setAllTagsList] = useState([])
  // Stores tag search value to fetch searched tags
  const [searchTagsFilterText, setSearchTagsFilterText] = useState('')
  // Stores subscription tier list api error
  const [subscriptionTiersApiError, setSubscriptionTiersApiError] =
    useState(null)
  // Stores tag list api error
  const [tagsApiError, setTagsApiError] = useState(null)

  // To get list of subscription tiers
  useEffect(
    () => {
      let isCurrent = true
      getSubscriptionTiers({ LDFlags, oidcUser }).then(
        (response) => {
          let filteredSubscriptionTierList = response
          if (productType === 'SERVICE') {
            filteredSubscriptionTierList = filteredSubscriptionTierList?.filter(
              (tier) =>
                serviceSubscriptionTiersLD?.serviceSubscriptionTiers?.includes(
                  tier?.valueName
                ) || isGLOP()
            )
          }

          if (!isCurrent) return
          setSubscriptionTierList(filteredSubscriptionTierList || [])
        },
        (error) => {
          setSubscriptionTiersApiError(
            displayApiError(error, t, setSubscriptionTiersApiError)
          )
        }
      )
      return () => {
        isCurrent = false
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      oidcUser.access_token,
      dmSilverPeakFlag,
      dmPCEFlag,
      dmUXIFlag,
      dmBridgeFlag
    ]
  )

  // To get list of tags if subscription tags flag is turned on
  useEffect(() => {
    if (subscriptionTagsFlag || isGLOP()) {
      getTags(
        oidcUser.access_token,
        t,
        setAllTagsList,
        setTagsApiError,
        productType === 'SERVICE',
        searchTagsFilterText
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    oidcUser.access_token,
    subscriptionTagsFlag,
    t,
    refreshTags,
    searchTagsFilterText
  ])

  return (
    <>
      <FilterButton
        dialogPosition="right"
        dialogHeight="100%"
        title={
          productType === 'SERVICE'
            ? t('licensing:service_subscription_filter')
            : t('licensing:device_subscription_filter')
        }
        initSelectedFilterValues={
          // exclude the query param that are not part of Filter button
          tabName === 'totalSubscriptions'
            ? filterOptions
            : omit(filterOptions, [
                'end_date_in_millis',
                'expire_date_cut_off_in_millis',
                'evaluation_type'
              ])
        }
        filterAttributes={getSubscriptionFilterOptions(
          subscriptionTierList,
          t,
          allTagsList,
          subscriptionTagsFlag || isGLOP(),
          LDFlags,
          tabName === 'totalSubscriptions',
          setSearchTagsFilterText
        )}
        onFilterValuesChange={(filters) => {
          // fetch the query param based on tab name
          const modifiedFilters = {
            ...filters,
            ...(tabName !== 'totalSubscriptions' && {
              ...(queryParamsBasedOnTab(tabName) || {})
            })
          }
          onFilterValueChange(modifiedFilters)
        }}
        testId="filter-search-btn"
        clearFilterString={t('common:clear_filter')}
      />
      {subscriptionTiersApiError}
      {tagsApiError}
    </>
  )
}

SubscriptionsFilterButton.propTypes = {
  tabName: PropTypes.string,
  productType: PropTypes.string,
  onFilterValueChange: PropTypes.func.isRequired,
  refreshTags: PropTypes.bool.isRequired,
  filterOptions: PropTypes.object.isRequired
}

export default SubscriptionsFilterButton
